// Our simple lexer, for now we only care about checking for "bad" (::blah or just :blah)
// tokens outside of quotes. For this purpose we will split our string into a list of:
// one of the following token types:
// * QuotedString - for now presumed to be a literal and not subject to inspection/validation
//   it can contain any other chars except the (opening) quote, and that itself can also
//   be included if escaped via backslah prefix (\). This token ends either with an unescaped opening quote
//   or can remain unclosed as the last token (which is invalid)
// * WhiteSpace - self-explanatory, preserved by the lexer, this would be needed for our
//   future "Raw SQL Editor"
// * SqlToken - stuff like '(', ')', ',', '<', '>', '=', In the future this could
//   include SQL words for syntax highlighting
// * UserToken - everything else
export enum TokenKind {
  SqlToken,
  QuotedString,
  WhiteSpace,
  UserToken,
}

export interface Token {
  kind: TokenKind;
  start: number;
  end: number;
}

const SQL_CHAR_TOKENS = new Set([
  // Comparison
  '=',
  '<',
  '>',
  '!',
  // Math
  '+',
  '-',
  '*',
  '/',
  '%',
  // Logic/precedence
  '(',
  ')',
  ',',
  '&',
  '|',
  '^',
  '!',
  // index/key reference
  '[',
  ']',
  // special/forbidden outside quotes characters
  // XXX the column is forbidden outside quotes too as it can break Spqing
  // named parameter JDBC template syntax (:param1)
  ';',
  ':',
]);

// The colon char is special as it can break Spring JDBC named parameters syntax (:param1)
const COLON_CHAR = ':';
const UNSAFE_COLON_ERROR_MESSAGE =
  'A single colon character followed by identifier can not be used ' +
  "outside quotes. If you are trying to use colon-based JSON path syntax (json:key), please use json['key'] instead";

const FORBIDDEN_TOKENS: Record<string, string | undefined> = {
  ';': 'The semicolon character can not be used outside quotes.',
};

const SQL_QUOTES = new Set(["'", '"', '`']);

const ESCAPE_CHAR = '\\';

const IS_SPACE_REGEX = /^\s+$/;

// does not include numbers
const IS_ALPHA_REGEX = /^[A-Za-z_]+$/;

const isWhitespace = (s: string) => IS_SPACE_REGEX.test(s);
const isQuote = (s: string) => SQL_QUOTES.has(s);
const isSqlCharToken = (s: string) => SQL_CHAR_TOKENS.has(s);

const isUnclosedQuote = (s: string) =>
  isQuote(s[0]) && (s.length === 1 || s[0] !== s[s.length - 1]);

export const tokenizeSql = (input: string): Token[] => {
  const ret: Token[] = [];
  let cur = 0;
  let currentStart = 0;
  // null - outside quotes
  let quoteType: string | null = null;
  let escaped = false;

  const curChar = () => input[cur];
  const handleEscapeChar = () => {
    if (curChar() === ESCAPE_CHAR) {
      escaped = !escaped;
    }
  };
  const pushWordToken = () => {
    if (currentStart < cur) {
      // non-empty token
      // TODO This could SqlToken in the future
      ret.push({
        kind: TokenKind.UserToken,
        start: currentStart,
        end: cur - 1,
      });
    }
    currentStart = cur;
  };
  const pushQuoteToken = (unclosed: boolean) => {
    const end = unclosed ? cur - 1 : cur;
    ret.push({ kind: TokenKind.QuotedString, start: currentStart, end });
    currentStart = end + 1;
  };
  const pushCharToken = (kind: TokenKind) => {
    ret.push({ kind, start: cur, end: cur });
    currentStart = cur + 1;
  };

  while (cur < input.length) {
    const char = curChar();
    handleEscapeChar();
    if (isQuote(char)) {
      if (quoteType === char) {
        // closing quote, if not escaped
        if (!escaped) {
          pushQuoteToken(false);
          quoteType = null;
        }
      } else {
        if (quoteType === null) {
          // start of quoted string, first push any pending word token
          pushWordToken();
          quoteType = char;
        }
      }
    } else if (quoteType === null) {
      // outside quotes
      if (isWhitespace(char)) {
        pushWordToken();
        pushCharToken(TokenKind.WhiteSpace);
      }
      if (isSqlCharToken(char)) {
        pushWordToken();
        pushCharToken(TokenKind.SqlToken);
      }
    } // else nothing - swallow everything inside quotes
    cur++;
  }
  if (!quoteType) {
    pushWordToken();
  } else {
    // unfinished quoted string
    pushQuoteToken(true);
  }
  return ret;
};

interface SqlValidationError {
  start: number;
  token: string;
  message: string;
}

const getTokenStr = (input: string, token: Token): string =>
  input.slice(token.start, token.end + 1);

const findNonAsciiCharPos = (s: string): number => {
  return s.split('').findIndex((char) => {
    const codePoint = char.codePointAt(0);
    return !codePoint || codePoint > 127;
  });
};

const getNonAsciiCharError = (
  tokenStr: string,
  token: Token,
): SqlValidationError | null => {
  // no restrictions inside quotes
  if (token.kind === TokenKind.QuotedString) {
    return null;
  }
  const nonAsciiPos = findNonAsciiCharPos(tokenStr);
  if (nonAsciiPos < 0) {
    return null;
  }
  return {
    start: token.start + nonAsciiPos,
    token: tokenStr[nonAsciiPos],
    message: 'Non-ASCII character detected outside quotes',
  };
};

export const validateRawSql = (input: string): SqlValidationError[] => {
  // a helper to construct a token error
  const tokenError = (token: Token | null, message: string) => {
    return {
      start: token?.start ?? 0,
      token: token ? getTokenStr(input, token) : ' ',
      message,
    };
  };

  const tokenized = tokenizeSql(input);

  // check for empty
  if (
    tokenized.length === 0 ||
    tokenized.every(({ kind }) => kind === TokenKind.WhiteSpace)
  ) {
    return [
      tokenError(
        null,
        'SQL statement can not be empty neither contain only whitespace',
      ),
    ];
  }

  // check for unclosed quote at the end
  const last = tokenized[tokenized.length - 1];
  const lastTokenStr = getTokenStr(input, last);
  if (isUnclosedQuote(lastTokenStr)) {
    return [tokenError(last, 'Unclosed quoted expression')];
  }

  // check individual tokens for known issues

  // a couple of helpers
  const prevTokenStr = (index: number) =>
    index > 1 ? getTokenStr(input, tokenized[index - 1]) : undefined;
  const nextCharIsAlpha = (token: Token) => {
    const nextIndex = token.end + 1;
    return nextIndex < input.length
      ? IS_ALPHA_REGEX.test(input[nextIndex])
      : false;
  };

  return tokenized.flatMap((token, index) => {
    const tokenStr = getTokenStr(input, token);

    // a special case for the colon char - a single colon followed by a word token and not preceded
    // by another colon is not allowed (breaks named jdbc params). I.e.:
    // blah:blah is bad
    // blah::blah is fine
    const isUnsafeColonFollowedByAlpha =
      tokenStr === COLON_CHAR &&
      nextCharIsAlpha(token) &&
      prevTokenStr(index) !== COLON_CHAR;
    if (isUnsafeColonFollowedByAlpha) {
      return [tokenError(token, UNSAFE_COLON_ERROR_MESSAGE)];
    }

    // forbidden tokens outside quotes
    const forbiddenTokenMessage = FORBIDDEN_TOKENS[tokenStr];
    if (forbiddenTokenMessage) {
      return [tokenError(token, forbiddenTokenMessage)];
    }

    // non-ascii chars outside quotes
    const nonAsciiError = getNonAsciiCharError(tokenStr, token);
    if (nonAsciiError) {
      return [nonAsciiError];
    }

    // no issues found
    return [];
  });
};
